<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import {
    getCountryList,
    markerStyles,
    maxTierForGpu,
    TIER_ORDER,
    type Country,
    type FillColorTokens,
    type FillState,
    type MapEngine,
    type MarkerPresetName,
    type PerfTimings,
    type TextureTier,
  } from '@atlasguess/engine';
  import { createAudio, type AudioPlayer } from '@atlasguess/audio';

  interface Props {
    engine: MapEngine | undefined;
    open: boolean;
    onClose: () => void;
    currentTier: TextureTier | 'loading';
    lastLoadMs: number;
    gpuMaxTextureSize: number;
    hoveredCountry: Country | null;
    lastClickedCountry: Country | null;
  }

  let {
    engine,
    open,
    onClose,
    currentTier,
    lastLoadMs,
    gpuMaxTextureSize,
    hoveredCountry,
    lastClickedCountry,
  }: Props = $props();

  const tiers: TextureTier[] = ['small', 'medium', 'large', 'xl'];

  const TIER_DIMENSIONS: Record<TextureTier, string> = {
    small: '2048 × 1024',
    medium: '4096 × 2048',
    large: '8192 × 4096',
    xl: '16384 × 8192',
  };

  let loading = $state(false);

  // Tiers above the GPU's MAX_TEXTURE_SIZE silently fail or render corrupted
  // (upside-down on some drivers). The engine refuses these too, but we
  // disable the buttons here so the user knows the option is unavailable
  // rather than clicking and getting a no-op or visual glitch.
  const maxAllowedRank = $derived(TIER_ORDER.indexOf(maxTierForGpu(gpuMaxTextureSize)));
  function isTierAllowed(tier: TextureTier): boolean {
    return TIER_ORDER.indexOf(tier) <= maxAllowedRank;
  }

  async function loadTier(tier: TextureTier): Promise<void> {
    if (!engine || loading || !isTierAllowed(tier)) return;
    loading = true;
    try {
      await engine.loadTier(tier);
    } finally {
      loading = false;
    }
  }

  const isMobileUa = $derived(
    typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent),
  );

  // -- Data section --
  let countries = $state<Country[] | null>(null);
  let dataError = $state('');

  onMount(() => {
    getCountryList()
      .then((list) => {
        countries = list;
      })
      .catch((err: Error) => {
        dataError = err.message;
      });
  });

  const countryCount = $derived(countries?.length ?? 0);
  const needsDotCount = $derived(countries?.filter((c) => c.needs_dot).length ?? 0);
  const continentCount = $derived(
    countries ? new Set(countries.map((c) => c.continent)).size : 0,
  );

  // -- Borders section --
  let bordersVisible = $state(true);
  let bordersReady = $state(false);
  let bordersPollInterval: ReturnType<typeof setInterval> | undefined;

  onMount(() => {
    // Poll engine for border readiness — they load async after engine init.
    // 200ms is frequent enough for the UI to feel snappy without cost.
    bordersPollInterval = setInterval(() => {
      if (!engine) return;
      const ready = engine.getBordersReady();
      if (ready && !bordersReady) {
        bordersReady = true;
        bordersVisible = engine.getBordersVisible();
        // Fills + their opacity are configured by loadBorders too — re-read
        // here so the panel mirrors the engine's post-load reality (the
        // earlier onMount read in the Fills block can fire before
        // loadBorders resolves, leaving stale defaults).
        fillsVisible = engine.layers.outline.isFillsVisible();
        fillsOpacity = engine.layers.outline.getFillOpacity();
      }
    }, 200);
    return () => {
      if (bordersPollInterval) clearInterval(bordersPollInterval);
    };
  });

  // Borders are lazy-loaded (game modes opt in). Toggling the panel button
  // before they've loaded triggers the fetch + sets visible to true so the
  // user sees borders as soon as they're ready. Subsequent clicks toggle
  // visibility normally.
  let bordersLoadingViaToggle = $state(false);

  async function toggleBorders(): Promise<void> {
    if (!engine || bordersLoadingViaToggle) return;
    if (!bordersReady) {
      bordersLoadingViaToggle = true;
      try {
        await engine.loadBorders();
        bordersVisible = true;
        engine.setBordersVisible(true);
      } catch (err) {
        console.error('[atlas] borders load failed', err);
      } finally {
        bordersLoadingViaToggle = false;
      }
      return;
    }
    bordersVisible = !bordersVisible;
    engine.setBordersVisible(bordersVisible);
  }

  // -- US admin1 (state borders) --
  // Three-state: 'unloaded' → user has never clicked it. 'loading' → fetch
  // in progress. 'visible' / 'hidden' → loaded; toggle flips between.
  let usAdmin1Status: 'unloaded' | 'loading' | 'visible' | 'hidden' = $state('unloaded');

  async function toggleUSAdmin1(): Promise<void> {
    if (!engine) return;
    if (usAdmin1Status === 'loading') return;

    if (usAdmin1Status === 'unloaded') {
      usAdmin1Status = 'loading';
      try {
        await engine.loadAdmin1('US');
        engine.setAdmin1Visible('US', true);
        usAdmin1Status = 'visible';
      } catch (err) {
        console.error('[atlas] US admin1 load failed', err);
        usAdmin1Status = 'unloaded';
      }
      return;
    }

    if (usAdmin1Status === 'visible') {
      engine.setAdmin1Visible('US', false);
      usAdmin1Status = 'hidden';
    } else if (usAdmin1Status === 'hidden') {
      engine.setAdmin1Visible('US', true);
      usAdmin1Status = 'visible';
    }
  }

  // -- Interactions section --
  let hoverEnabled = $state(true);
  let clickEnabled = $state(true);
  let interactionsReady = $state(false);

  onMount(() => {
    // Interactions wire up alongside borders (same data load) — poll for
    // readiness same as the borders section does.
    const interval = setInterval(() => {
      if (!engine) return;
      if (engine.getInteractionsReady() && !interactionsReady) {
        interactionsReady = true;
      }
    }, 200);
    return () => clearInterval(interval);
  });

  function toggleHover(): void {
    if (!engine || !interactionsReady) return;
    hoverEnabled = !hoverEnabled;
    engine.setHoverEnabled(hoverEnabled);
  }

  function toggleClick(): void {
    if (!engine || !interactionsReady) return;
    clickEnabled = !clickEnabled;
    engine.setClickEnabled(clickEnabled);
  }

  // -- Markers: small-country (needs_dot) pins --
  // First click triggers loadBorders if polygons haven't been fetched yet
  // (since the country metadata that drives needs_dot lives in the same
  // load), then auto-places the markers. Subsequent clicks just toggle
  // layer visibility.
  let needsDotStatus: 'unloaded' | 'visible' | 'hidden' = $state('unloaded');

  async function toggleNeedsDotDots(): Promise<void> {
    if (!engine || bordersLoadingViaToggle) return;
    if (!bordersReady) {
      bordersLoadingViaToggle = true;
      try {
        await engine.loadBorders();
        engine.autoPlaceNeedsDotMarkers('country_pins');
        engine.markers.setLayerVisible('country_pins', true);
        needsDotStatus = 'visible';
      } catch (err) {
        console.error('[atlas] borders load failed (from needs-dot toggle)', err);
      } finally {
        bordersLoadingViaToggle = false;
      }
      return;
    }
    if (needsDotStatus === 'unloaded') {
      engine.autoPlaceNeedsDotMarkers('country_pins');
      engine.markers.setLayerVisible('country_pins', true);
      needsDotStatus = 'visible';
      return;
    }
    if (needsDotStatus === 'visible') {
      engine.markers.setLayerVisible('country_pins', false);
      needsDotStatus = 'hidden';
    } else {
      engine.markers.setLayerVisible('country_pins', true);
      needsDotStatus = 'visible';
    }
  }

  // -- Texture visibility (the tier buttons live above; this is the on/off
  // toggle independent of which tier is loaded).
  let textureVisible = $state(true);

  function toggleTexture(): void {
    if (!engine) return;
    textureVisible = !textureVisible;
    // setOpacity instead of setVisible so the layer fades in/out smoothly.
    engine.layers.texture.setOpacity(textureVisible ? 1 : 0);
  }

  // -- Fills section --
  // Defaults match the engine's post-init reality (loadBorders flips fills
  // off after the meshes build). The bordersReady poll re-reads both
  // values once loadBorders resolves — these defaults just cover the brief
  // pre-poll window so the panel never claims fills are on when they're not.
  let fillsVisible = $state(false);
  let fillsOpacity = $state(0.55);
  // Color tokens are seeded from engine defaults on first mount and become
  // the source of truth for the color pickers. Engine writes flow one-way
  // out from the pickers — no two-way sync needed.
  let fillTokens = $state<FillColorTokens>({
    base: '#4a5568',
    hover: '#7589a8',
    selected: '#5fb6cd',
    correct: '#7fb069',
    incorrect: '#d97757',
    revealed: '#d4a24c',
    inactive: '#252a32',
  });

  onMount(() => {
    // Pull live tokens once the engine handle is set so we display the
    // engine's actual defaults if they ever drift from the literals above.
    if (engine) {
      fillTokens = engine.layers.outline.getColorTokens();
      fillsVisible = engine.layers.outline.isFillsVisible();
      fillsOpacity = engine.layers.outline.getFillOpacity();
    }
  });

  // Fills depend on polygons being loaded — toggling on before borders are
  // ready triggers loadBorders first, then turns the fills layer on.
  async function toggleFills(): Promise<void> {
    if (!engine || bordersLoadingViaToggle) return;
    if (!bordersReady) {
      bordersLoadingViaToggle = true;
      try {
        await engine.loadBorders();
        fillsVisible = true;
        engine.layers.outline.setFillsVisible(true);
      } catch (err) {
        console.error('[atlas] borders load failed (from fills toggle)', err);
      } finally {
        bordersLoadingViaToggle = false;
      }
      return;
    }
    fillsVisible = !fillsVisible;
    engine.layers.outline.setFillsVisible(fillsVisible);
  }

  function onFillsOpacityInput(e: Event): void {
    if (!engine) return;
    const value = Number((e.target as HTMLInputElement).value) / 100;
    fillsOpacity = value;
    // No animation — slider drag should track 1:1 with the cursor.
    engine.layers.outline.setFillOpacity(value, false);
  }

  function onColorPick(token: keyof FillColorTokens, value: string): void {
    if (!engine) return;
    fillTokens = { ...fillTokens, [token]: value };
    engine.layers.outline.setColorTokens({ [token]: value });
  }

  // -- Fill state cycle: walks one country through every state to eyeball
  // each color + transition. Uses last-clicked country, falling back to FR.
  const CYCLE_STATES: (FillState | null)[] = [
    'selected',
    'correct',
    'incorrect',
    'revealed',
    null,
  ];
  const CYCLE_STEP_MS = 700;
  let cycling = $state(false);
  let cycleTimer: ReturnType<typeof setInterval> | undefined;

  function startStateCycle(): void {
    if (!engine || cycling) return;
    const iso = lastClickedCountry?.iso_a2 ?? 'FR';
    cycling = true;
    let i = 0;
    // Apply first state immediately so the user sees motion right away.
    engine.layers.outline.setFillState(iso, CYCLE_STATES[i]);
    cycleTimer = setInterval(() => {
      if (!engine) {
        stopStateCycle();
        return;
      }
      i++;
      if (i >= CYCLE_STATES.length) {
        stopStateCycle();
        return;
      }
      engine.layers.outline.setFillState(iso, CYCLE_STATES[i]);
    }, CYCLE_STEP_MS);
  }

  function stopStateCycle(): void {
    if (cycleTimer) {
      clearInterval(cycleTimer);
      cycleTimer = undefined;
    }
    cycling = false;
  }

  onDestroy(stopStateCycle);

  // -- Performance section --
  // Polls engine.getPerfTimings() so phase durations show up as soon as each
  // one completes. 500ms is plenty — these only change on init / on tier
  // upgrades, not per-frame.
  let perf = $state<PerfTimings>({
    engineConstructorMs: null,
    textures: {},
    polygonsFetchMs: null,
    polygonsDecodeMs: null,
    fillsBuildMs: null,
  });
  let perfInterval: ReturnType<typeof setInterval> | undefined;
  const TIER_LIST: TextureTier[] = ['small', 'medium', 'large', 'xl'];

  onMount(() => {
    perfInterval = setInterval(() => {
      if (engine) perf = engine.getPerfTimings();
    }, 500);
    return () => {
      if (perfInterval) clearInterval(perfInterval);
    };
  });

  function fmtMs(ms: number | null): string {
    if (ms === null) return '—';
    return ms < 10 ? `${ms.toFixed(1)} ms` : `${Math.round(ms)} ms`;
  }

  // -- Test fixtures (verification) --
  // Drops one of each marker preset on a fixed lat/lon ring around the globe
  // so the visual style of every preset is checkable at a glance. Layer
  // name `_test_markers` is intentionally underscore-prefixed so it doesn't
  // collide with anything a real game mode might use.
  const SAMPLE_MARKERS: { name: MarkerPresetName; lat: number; lon: number }[] = [
    { name: 'click', lat: 40.71, lon: -74.0 }, // New York
    { name: 'location', lat: 51.51, lon: -0.13 }, // London
    { name: 'history', lat: 35.69, lon: 139.69 }, // Tokyo
    { name: 'correct', lat: -33.87, lon: 151.21 }, // Sydney
    { name: 'incorrect', lat: -23.55, lon: -46.63 }, // São Paulo
    { name: 'countryPin', lat: 0.39, lon: 9.45 }, // Equatorial Guinea
    { name: 'cityCapital', lat: 48.85, lon: 2.35 }, // Paris
    { name: 'cityMinor', lat: 55.75, lon: 37.62 }, // Moscow
  ];
  let sampleMarkersVisible = $state(false);

  function toggleSampleMarkers(): void {
    if (!engine) return;
    if (sampleMarkersVisible) {
      engine.markers.clear('_test_markers');
      sampleMarkersVisible = false;
      return;
    }
    for (const m of SAMPLE_MARKERS) {
      engine.markers.add({
        id: `_test_marker_${m.name}`,
        position: { lat: m.lat, lon: m.lon },
        style: markerStyles[m.name],
        layer: '_test_markers',
      });
    }
    engine.markers.setLayerVisible('_test_markers', true);
    sampleMarkersVisible = true;
  }

  // Lazy AudioPlayer — created on first sound test so we don't construct
  // an AudioContext until the user actually wants audio (most browsers
  // hold the context suspended until a user gesture, but creating one
  // unprompted still feels heavyweight).
  let audio: AudioPlayer | undefined;
  function getAudio(): AudioPlayer {
    if (!audio) audio = createAudio();
    return audio;
  }
  onDestroy(() => audio?.dispose());

  /** One tick + one ding — sanity check that Web Audio is wired up. */
  function playTestTickDing(): void {
    const a = getAudio();
    a.tick();
    a.ding({ delaySec: 0.15 });
  }

  /** Full travel sequence — verifies tick scheduling + duration scaling. */
  function playTestTravelSequence(): void {
    const a = getAudio();
    // ~1500ms travel + a C-major chord ding ("great guess" vibe).
    a.playSequence({
      durationMs: 1500,
      dingFrequencies: [523.25, 659.25, 783.99],
    });
  }

  /**
   * Draw an animated arc between two well-known points and run the audio
   * sequence in sync. End-to-end test of the integration: arc primitive,
   * progress timing, audio scheduling. Verifies the moving parts will
   * combine cleanly when a real city-distance game wires them up.
   */
  const TEST_ARC_DURATION_MS = 1800;
  function playTestArc(): void {
    if (!engine) return;
    const a = getAudio();
    a.playSequence({
      durationMs: TEST_ARC_DURATION_MS,
      dingFrequencies: [659.25],
    });
    engine.arcs.draw({
      from: { lat: 40.7128, lon: -74.006 }, // New York
      to: { lat: 51.5074, lon: -0.1278 }, // London
      durationMs: TEST_ARC_DURATION_MS,
      removeOnComplete: false,
    });
  }
</script>

<aside
  class="debug-panel"
  class:debug-panel--open={open}
  aria-label="Debug panel"
  aria-hidden={!open}
>
  <header class="debug-panel__header">
    <span class="debug-panel__title">Debug</span>
    <button class="debug-panel__close" onclick={onClose} aria-label="Close debug panel">
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M2 2 L10 10 M10 2 L2 10" stroke="currentColor" stroke-width="1.5" fill="none" />
      </svg>
    </button>
  </header>

  <section class="debug-panel__section">
    <h3 class="debug-panel__section-title">Texture</h3>

    <div class="debug-panel__tier-grid">
      {#each tiers as tier}
        {@const allowed = isTierAllowed(tier)}
        <button
          class="debug-panel__tier-btn"
          class:debug-panel__tier-btn--active={currentTier === tier}
          disabled={loading || !allowed}
          title={allowed ? '' : `Exceeds GPU max ${gpuMaxTextureSize}`}
          onclick={() => loadTier(tier)}
        >
          <span class="debug-panel__tier-name">{tier}</span>
          <span class="debug-panel__tier-dim">{TIER_DIMENSIONS[tier]}</span>
        </button>
      {/each}
    </div>

    <div class="debug-panel__kv-group">
      <button
        class="debug-panel__toggle"
        class:debug-panel__toggle--on={textureVisible}
        onclick={toggleTexture}
      >
        <span class="debug-panel__toggle-label">Photo texture</span>
        <span class="debug-panel__toggle-state">{textureVisible ? 'on' : 'off'}</span>
      </button>
      <div class="debug-panel__kv">
        <span class="debug-panel__k">Current</span>
        <span class="debug-panel__v">{currentTier}</span>
      </div>
      <div class="debug-panel__kv">
        <span class="debug-panel__k">Last load</span>
        <span class="debug-panel__v">{lastLoadMs > 0 ? `${lastLoadMs} ms` : '—'}</span>
      </div>
    </div>
  </section>

  <section class="debug-panel__section">
    <h3 class="debug-panel__section-title">Borders</h3>
    <div class="debug-panel__kv-group">
      <button
        class="debug-panel__toggle"
        class:debug-panel__toggle--on={bordersVisible && bordersReady}
        disabled={bordersLoadingViaToggle}
        onclick={toggleBorders}
      >
        <span class="debug-panel__toggle-label">Country borders</span>
        <span class="debug-panel__toggle-state">
          {#if bordersLoadingViaToggle}loading
          {:else if !bordersReady}load
          {:else if bordersVisible}on
          {:else}off{/if}
        </span>
      </button>
      <button
        class="debug-panel__toggle"
        class:debug-panel__toggle--on={usAdmin1Status === 'visible'}
        disabled={usAdmin1Status === 'loading'}
        onclick={toggleUSAdmin1}
      >
        <span class="debug-panel__toggle-label">US state borders</span>
        <span class="debug-panel__toggle-state">
          {#if usAdmin1Status === 'unloaded'}load
          {:else if usAdmin1Status === 'loading'}loading
          {:else if usAdmin1Status === 'visible'}on
          {:else}off{/if}
        </span>
      </button>
    </div>
  </section>

  <section class="debug-panel__section">
    <h3 class="debug-panel__section-title">Fills</h3>
    <div class="debug-panel__kv-group">
      <button
        class="debug-panel__toggle"
        class:debug-panel__toggle--on={fillsVisible && bordersReady}
        disabled={bordersLoadingViaToggle}
        onclick={toggleFills}
      >
        <span class="debug-panel__toggle-label">Country fills</span>
        <span class="debug-panel__toggle-state">
          {#if bordersLoadingViaToggle}loading
          {:else if !bordersReady}load
          {:else if fillsVisible}on
          {:else}off{/if}
        </span>
      </button>
    </div>

    <label class="debug-panel__slider">
      <span class="debug-panel__slider-label">Opacity</span>
      <input
        type="range"
        min="0"
        max="100"
        value={Math.round(fillsOpacity * 100)}
        disabled={!bordersReady}
        oninput={onFillsOpacityInput}
      />
      <span class="debug-panel__slider-value">{Math.round(fillsOpacity * 100)}%</span>
    </label>

    <div class="debug-panel__color-grid">
      {#each Object.entries(fillTokens) as [token, color]}
        <label class="debug-panel__color-row">
          <span class="debug-panel__color-label">{token}</span>
          <input
            type="color"
            value={color}
            disabled={!bordersReady}
            oninput={(e) =>
              onColorPick(token as keyof FillColorTokens, (e.target as HTMLInputElement).value)}
          />
        </label>
      {/each}
    </div>

    <button
      class="debug-panel__toggle"
      disabled={!bordersReady || cycling}
      onclick={startStateCycle}
    >
      <span class="debug-panel__toggle-label">
        Cycle states on {lastClickedCountry?.iso_a2 ?? 'FR'}
      </span>
      <span class="debug-panel__toggle-state">
        {cycling ? 'running' : 'run'}
      </span>
    </button>
  </section>

  <section class="debug-panel__section">
    <h3 class="debug-panel__section-title">Markers</h3>
    <button
      class="debug-panel__toggle"
      class:debug-panel__toggle--on={needsDotStatus === 'visible'}
      disabled={bordersLoadingViaToggle}
      onclick={toggleNeedsDotDots}
    >
      <span class="debug-panel__toggle-label">Small-country dots</span>
      <span class="debug-panel__toggle-state">
        {#if bordersLoadingViaToggle}loading
        {:else if !bordersReady}load
        {:else if needsDotStatus === 'unloaded'}place
        {:else if needsDotStatus === 'visible'}on
        {:else}off{/if}
      </span>
    </button>
  </section>

  <section class="debug-panel__section">
    <h3 class="debug-panel__section-title">Interactions</h3>
    <div class="debug-panel__kv-group">
      <button
        class="debug-panel__toggle"
        class:debug-panel__toggle--on={hoverEnabled && interactionsReady}
        disabled={!interactionsReady}
        onclick={toggleHover}
      >
        <span class="debug-panel__toggle-label">Country hover</span>
        <span class="debug-panel__toggle-state"
          >{interactionsReady ? (hoverEnabled ? 'on' : 'off') : 'loading'}</span
        >
      </button>
      <button
        class="debug-panel__toggle"
        class:debug-panel__toggle--on={clickEnabled && interactionsReady}
        disabled={!interactionsReady}
        onclick={toggleClick}
      >
        <span class="debug-panel__toggle-label">Country click</span>
        <span class="debug-panel__toggle-state"
          >{interactionsReady ? (clickEnabled ? 'on' : 'off') : 'loading'}</span
        >
      </button>
    </div>
    <div class="debug-panel__kv-group debug-panel__kv-group--spaced">
      <div class="debug-panel__kv">
        <span class="debug-panel__k">Hovered</span>
        <span class="debug-panel__v">
          {hoveredCountry ? `${hoveredCountry.name} (${hoveredCountry.iso_a2})` : '—'}
        </span>
      </div>
      <div class="debug-panel__kv">
        <span class="debug-panel__k">Last click</span>
        <span class="debug-panel__v">
          {lastClickedCountry
            ? `${lastClickedCountry.name} (${lastClickedCountry.iso_a2})`
            : '—'}
        </span>
      </div>
    </div>
  </section>

  <section class="debug-panel__section">
    <h3 class="debug-panel__section-title">Data</h3>
    {#if dataError}
      <div class="debug-panel__kv">
        <span class="debug-panel__k">Error</span>
        <span class="debug-panel__v">{dataError}</span>
      </div>
    {:else if countries === null}
      <div class="debug-panel__kv">
        <span class="debug-panel__k">Countries</span>
        <span class="debug-panel__v">loading</span>
      </div>
    {:else}
      <div class="debug-panel__kv-group">
        <div class="debug-panel__kv">
          <span class="debug-panel__k">Countries</span>
          <span class="debug-panel__v">{countryCount}</span>
        </div>
        <div class="debug-panel__kv">
          <span class="debug-panel__k">Continents</span>
          <span class="debug-panel__v">{continentCount}</span>
        </div>
        <div class="debug-panel__kv">
          <span class="debug-panel__k">Needs dot</span>
          <span class="debug-panel__v">{needsDotCount}</span>
        </div>
      </div>
    {/if}
  </section>

  <section class="debug-panel__section">
    <h3 class="debug-panel__section-title">Device</h3>
    <div class="debug-panel__kv-group">
      <div class="debug-panel__kv">
        <span class="debug-panel__k">GPU max</span>
        <span class="debug-panel__v">{gpuMaxTextureSize}</span>
      </div>
      <div class="debug-panel__kv">
        <span class="debug-panel__k">UA mobile</span>
        <span class="debug-panel__v">{isMobileUa ? 'yes' : 'no'}</span>
      </div>
      <div class="debug-panel__kv">
        <span class="debug-panel__k">DPR</span>
        <span class="debug-panel__v">{typeof window !== 'undefined' ? window.devicePixelRatio : '—'}</span>
      </div>
    </div>
  </section>

  <section class="debug-panel__section">
    <h3 class="debug-panel__section-title">Performance</h3>
    <div class="debug-panel__kv-group">
      <div class="debug-panel__kv">
        <span class="debug-panel__k">FPS</span>
        <span class="debug-panel__v">{engine?.getFPS() ?? '—'}</span>
      </div>
      <div class="debug-panel__kv">
        <span class="debug-panel__k">Engine ctor</span>
        <span class="debug-panel__v">{fmtMs(perf.engineConstructorMs)}</span>
      </div>
      {#each TIER_LIST as tier}
        {@const t = perf.textures[tier]}
        {#if t}
          <div class="debug-panel__kv">
            <span class="debug-panel__k">Tex {tier}</span>
            <span class="debug-panel__v">{fmtMs(t.fetchMs)}</span>
          </div>
        {/if}
      {/each}
      <div class="debug-panel__kv">
        <span class="debug-panel__k">Polys fetch</span>
        <span class="debug-panel__v">{fmtMs(perf.polygonsFetchMs)}</span>
      </div>
      <div class="debug-panel__kv">
        <span class="debug-panel__k">Polys decode</span>
        <span class="debug-panel__v">{fmtMs(perf.polygonsDecodeMs)}</span>
      </div>
      <div class="debug-panel__kv">
        <span class="debug-panel__k">Fills build</span>
        <span class="debug-panel__v">{fmtMs(perf.fillsBuildMs)}</span>
      </div>
    </div>
  </section>

  <section class="debug-panel__section">
    <h3 class="debug-panel__section-title">Test fixtures</h3>
    <div class="debug-panel__kv-group">
      <button
        class="debug-panel__toggle"
        class:debug-panel__toggle--on={sampleMarkersVisible}
        onclick={toggleSampleMarkers}
      >
        <span class="debug-panel__toggle-label">Sample marker presets</span>
        <span class="debug-panel__toggle-state">{sampleMarkersVisible ? 'on' : 'off'}</span>
      </button>
      <button class="debug-panel__toggle" onclick={playTestTickDing}>
        <span class="debug-panel__toggle-label">Tick + ding</span>
        <span class="debug-panel__toggle-state">play</span>
      </button>
      <button class="debug-panel__toggle" onclick={playTestTravelSequence}>
        <span class="debug-panel__toggle-label">Travel sequence (1.5s)</span>
        <span class="debug-panel__toggle-state">play</span>
      </button>
      <button class="debug-panel__toggle" onclick={playTestArc}>
        <span class="debug-panel__toggle-label">Arc + audio (NYC → London)</span>
        <span class="debug-panel__toggle-state">draw</span>
      </button>
    </div>
  </section>

</aside>
